import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { currencyLabelWhole } from "helpers";
import { AdvancedTokenInfo } from "./models";

interface AdvancedTokenOfferProps {
    meterNumber: string;
    advancedToken: AdvancedTokenInfo;
}

const ADVANCE_TERMS_TEXT =
    "Please note that this advance token will cause that a collection of this amount will be lodged including fees on top of that";

async function requestAdvancedToken(meterNumber: string, amountCents: number): Promise<string> {
    const res = await fetch(`/api/v1/meters/${encodeURIComponent(meterNumber)}/advanced-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amountCents }),
    });

    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(body.message || "Could not issue advance token. Please try again later.");
    }

    return body.vendId as string;
}

export default function AdvancedTokenOffer({ meterNumber, advancedToken }: AdvancedTokenOfferProps) {
    const navigate = useNavigate();
    const [loadingAmount, setLoadingAmount] = useState<number | null>(null);
    const [pendingAmount, setPendingAmount] = useState<number | null>(null);
    const [error, setError] = useState<string>();

    if (!advancedToken.visible) {
        return null;
    }

    const disabled = !advancedToken.offered || loadingAmount !== null;

    async function handleRequest(amountCents: number) {
        setError(undefined);
        setLoadingAmount(amountCents);
        try {
            const vendId = await requestAdvancedToken(meterNumber, amountCents);
            navigate(`/vends/${vendId}`);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not issue advance token.");
        } finally {
            setLoadingAmount(null);
        }
    }

    function openTerms(amountCents: number) {
        setError(undefined);
        setPendingAmount(amountCents);
    }

    function closeTerms() {
        setPendingAmount(null);
    }

    async function agreeToTerms() {
        if (pendingAmount == null) {
            return;
        }
        const amountCents = pendingAmount;
        setPendingAmount(null);
        await handleRequest(amountCents);
    }

    return (
        <>
            <section
                className={`rounded-xl border px-4 py-4 ${
                    advancedToken.offered ? "border-engage-300 bg-white" : "border-gray-200 bg-gray-50"
                }`}
                aria-labelledby="advance-token-heading"
            >
                <h2 id="advance-token-heading" className="text-sm font-bold text-engage-800">
                    Emergency advance token
                </h2>

                <p className="mt-2 text-sm leading-relaxed text-gray-700">
                    For emergencies only — if you cannot afford to pay right now or need to carry over to next month.
                    This will add a <strong>collection</strong> to your meter for the same amount and works out
                    <strong> more expensive</strong> than a normal vend.
                </p>

                {!advancedToken.offered && advancedToken.unavailableReason && (
                    <p className="mt-3 text-sm font-medium text-gray-600">{advancedToken.unavailableReason}</p>
                )}

                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                    {advancedToken.amountOptions.map((amountCents) => (
                        <button
                            key={amountCents}
                            type="button"
                            disabled={disabled}
                            onClick={() => openTerms(amountCents)}
                            className={`flex-1 rounded-lg border px-4 py-3 text-base font-bold touch-manipulation ${
                                advancedToken.offered
                                    ? "border-engage-500 bg-engage-400 text-white hover:bg-engage-500 active:bg-engage-600 disabled:opacity-60"
                                    : "cursor-not-allowed border-gray-300 bg-gray-200 text-gray-500"
                            }`}
                        >
                            {loadingAmount === amountCents
                                ? "Issuing token..."
                                : `Advance ${currencyLabelWhole(amountCents)}`}
                        </button>
                    ))}
                </div>

                {error && (
                    <p className="mt-3 text-sm font-medium text-red-700" role="alert">
                        {error}
                    </p>
                )}
            </section>

            {pendingAmount != null && (
                <div
                    className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
                    role="presentation"
                    onClick={closeTerms}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="advance-terms-title"
                        className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-5 shadow-xl"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <h3 id="advance-terms-title" className="text-lg font-bold text-engage-800">
                            Terms and Conditions
                        </h3>
                        <p className="mt-3 text-sm leading-relaxed text-gray-700">
                            {ADVANCE_TERMS_TEXT}
                        </p>
                        <p className="mt-3 text-sm font-semibold text-engage-800">
                            Advance amount: {currencyLabelWhole(pendingAmount)}
                        </p>
                        <div className="mt-5 flex flex-col gap-3 sm:flex-row-reverse">
                            <button
                                type="button"
                                onClick={() => void agreeToTerms()}
                                className="flex-1 rounded-lg border border-engage-500 bg-engage-400 px-4 py-3 text-base font-bold text-white hover:bg-engage-500 active:bg-engage-600 touch-manipulation"
                            >
                                I agree
                            </button>
                            <button
                                type="button"
                                onClick={closeTerms}
                                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-base font-bold text-gray-700 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
